import { regionOfOrg, RegionId } from "../config/regionMap";
import {
  canonicalOrgCode,
  DEFAULT_SUPPORT_ORG,
  supportOrgFor,
} from "../config/supportOrgs";

/**
 * Which organization a CORP user is connected to, and as whom — the one decision behind
 * both entry points (the org switcher, and restoring the last active tenant at login).
 * Pure: no side effects, no I/O. The side effects live in services/supportTenantRuntime.ts.
 *
 * TWO CASES, AND THEY ARE NOT THE SAME
 * ------------------------------------
 *  - Another org's environment  -> impersonate that org's support user (SUPPORT_<ORG>USER),
 *    which is what re-points SQL Row-Level Security at that tenant's rows.
 *  - Their OWN dedicated environment (org code === their own WHOTO) -> no override at all:
 *    they act as themselves, with their own permissions and their own audit trail. A CORP
 *    user in CORP is not a support user, so borrowing SUPPORT_CORPUSER's identity there
 *    would give them the wrong row set and attribute their work to a shared account.
 *
 * WHY THE DECISION IS ISOLATED HERE
 * ---------------------------------
 * Impersonation must happen for CORP/CORP2 and nobody else, so the predicate lives in one
 * function with nothing else in it — see supportTenant.test.ts, which walks every other
 * WHOTO and asserts it is refused.
 *
 * This is a UX gate, not the security boundary. The backend re-authorizes the override on
 * EVERY request (Security/SafeAmbient.cs: Graph companyName allow-list + the caller's own
 * WHOTO must be CORP/CORP2) and fails closed, so a forged saved tenant buys nothing. Keeping
 * the client strict means a non-CORP user never even sends the header.
 */

/** An organization to connect to, and the identity to connect as. */
export interface SupportTarget {
  /** Org / WHOTO code, canonical (upper-case). */
  code: string;
  /**
   * Ambient email that scopes RLS to that org, or **null to act as the user themselves** —
   * their own dedicated environment. null must be APPLIED, not skipped: coming back from
   * another org has to drop that org's impersonation.
   */
  support: string | null;
  /** Region whose Function App + database hosts the org; undefined ⇒ default BFF. */
  region?: RegionId;
}

/**
 * Is this WHOTO corp-capable? Mirrors the backend's RoleGuard.IsCorpWhoto (trimmed,
 * case-insensitive) rather than App.tsx's exact-match `userIsCorp`, so the client's decision
 * can't diverge from the server's on a value like "corp " that the backend would accept.
 * Deliberately NOT folded into userRole.ts: widening that predicate would widen the
 * CorpRoute guard too, which is a separate decision from this feature.
 */
export function isCorpWhoto(whoto?: string | null): boolean {
  const canonical = whoto?.trim().toUpperCase() ?? "";
  return canonical === "CORP" || canonical === "CORP2";
}

/**
 * Is this org the user's own dedicated environment (rather than one they support)?
 * CORP's own environment is CORP; CORP2's is CORP2 — a CORP user in CORP2 is still
 * supporting someone else's environment and does impersonate.
 */
export function isOwnEnvironment(whoto?: string | null, orgCode?: string | null): boolean {
  const own = canonicalOrgCode(whoto);
  return own !== "" && own === canonicalOrgCode(orgCode);
}

/**
 * How to connect a user to an organization, or null to leave the session alone.
 *
 * Returns null unless the user's WHOTO is CORP/CORP2 **and** the org is a known support
 * target (see SUPPORT_ORGS) — so an unknown, stale or tampered code can never synthesise an
 * ambient email of its own. Not flag-gated: the org switcher works regardless of the
 * auto-tenant flag, and "act as yourself in your own environment" is simply how
 * impersonation should behave.
 */
export function resolveSupportTarget(args: {
  whoto?: string | null;
  orgCode?: string | null;
  /**
   * The user has no dbo.Users row and is signed in as the shared support user
   * (IUserInfo.supportBootstrap). It suppresses the "act as yourself" case below: they have
   * no own identity to fall back to, so `support` is never null. See resolveAutoTenant.
   */
  unprovisioned?: boolean;
}): SupportTarget | null {
  const { whoto, orgCode, unprovisioned } = args;

  if (!isCorpWhoto(whoto)) return null;

  const org = supportOrgFor(orgCode);
  if (!org) return null;

  // `isOwnEnvironment` asks "does this user have their own place here?" — which is exactly
  // what a bootstrapped user does not. Their WHOTO is CORP because that is the support user's
  // WHOTO, not because they own a CORP row, so dropping the override in CORP would resolve
  // RLS against their own email, match no row, and empty every screen.
  const actAsSelf = !unprovisioned && isOwnEnvironment(whoto, org.code);

  return {
    code: org.code,
    support: actAsSelf ? null : org.support,
    region: regionOfOrg(org.code),
  };
}

/**
 * The organization to auto-connect to at login, or null to log in normally. Adds the Key
 * Vault feature flag on top of resolveSupportTarget — the flag governs the AUTOMATIC
 * placement only, not how impersonation itself works.
 *
 * A saved tenant equal to the user's own environment is still honoured (it restores their
 * home org with no override), so a saved value can never leave the session mid-way.
 */
export function resolveAutoTenant(args: {
  featureEnabled: boolean;
  whoto?: string | null;
  savedTenant?: string | null;
  /** IUserInfo.supportBootstrap — see below, this path ignores the feature flag. */
  unprovisioned?: boolean;
}): SupportTarget | null {
  // A bootstrapped session is placed FIRST and unconditionally. The flag governs a
  // convenience — restoring the org a corp user last supported — but for someone with no row
  // of their own the impersonation is not a convenience, it is the session. Honouring the
  // flag here would log them into a working application that shows them nothing, in the
  // environment where the flag happens to be off.
  //
  // They also have nowhere to save a tenant (dbo.Users.LastSupportTenant lives on the row
  // they don't have), so there is never a saved value to restore: they start in CORP and
  // switch from there like anyone else.
  if (args.unprovisioned) {
    const restored = resolveSupportTarget({
      whoto: args.whoto,
      orgCode: args.savedTenant,
      unprovisioned: true,
    });

    // An absent, stale or tampered code falls back to CORP rather than to null. For a
    // provisioned user null is the right answer — "stay in your own tenant" — but this user
    // has no own tenant, so null is an empty application. CORP is what they are entitled to
    // anyway, so the fallback can only narrow, never widen. Still routed through
    // resolveSupportTarget so the corp-only gate applies to the fallback too.
    return (
      restored ??
      resolveSupportTarget({
        whoto: args.whoto,
        orgCode: DEFAULT_SUPPORT_ORG,
        unprovisioned: true,
      })
    );
  }

  if (!args.featureEnabled) return null;

  return resolveSupportTarget({ whoto: args.whoto, orgCode: args.savedTenant });
}
